import { useCallback, useEffect, useMemo, useState } from "react";
import type { ChangelogEntry } from "@/types/changelog";
import { changelogEntryKey } from "@/lib/changelogStory";

export type ChangelogReviewState = "approved" | "rejected";

export interface ChangelogDecision {
  state: ChangelogReviewState;
  reason?: string;
  reviewed_at: string;
  /** In JIRA angelegte Story (Changelog-Popup v2, »In JIRA anlegen«). */
  jira_key?: string;
}

/** Anzeige-Status eines Eintrags (Status-Spalte der Changelog-Tabelle). */
export type ChangelogReviewStatus = "offen" | "approved" | "rejected" | "in-jira";

export function reviewStatusOf(d?: ChangelogDecision): ChangelogReviewStatus {
  if (d?.jira_key) return "in-jira";
  return d?.state ?? "offen";
}

type Payload = { decisions: Record<string, Record<string, ChangelogDecision>> };

const STORAGE_KEY = "maco.changelog-review.v1";

function load(): Payload {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { decisions: {} };
    const parsed = JSON.parse(raw);
    return { decisions: parsed.decisions ?? {} };
  } catch {
    return { decisions: {} };
  }
}

function save(payload: Payload) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* Quota — vernachlässigbar */
  }
}

export function useChangelogReviewState(runId: string) {
  const [payload, setPayload] = useState<Payload>(() => load());

  useEffect(() => {
    save(payload);
  }, [payload]);

  // Memo: ohne Entscheidungen im Run wäre `?? {}` in jedem Render ein neues
  // Objekt, getDecision damit instabil und jede darauf memoisierte Liste neu.
  const decisions = useMemo(() => payload.decisions[runId] ?? {}, [payload, runId]);

  const getDecision = useCallback(
    (e: ChangelogEntry): ChangelogDecision | undefined => decisions[changelogEntryKey(e)],
    [decisions]
  );

  const setDecision = useCallback(
    (e: ChangelogEntry, d: { state: ChangelogReviewState; reason?: string }) => {
      const key = changelogEntryKey(e);
      setPayload((prev) => {
        const run = { ...(prev.decisions[runId] ?? {}) };
        // Ein schon angelegtes JIRA-Ticket bleibt vermerkt — es existiert weiter.
        const jiraKey = run[key]?.jira_key;
        run[key] = {
          ...d,
          reviewed_at: new Date().toISOString(),
          ...(jiraKey ? { jira_key: jiraKey } : {}),
        };
        return { decisions: { ...prev.decisions, [runId]: run } };
      });
    },
    [runId]
  );

  /** Setzt Approve/Verwerfen zurück. Ein angelegtes JIRA-Ticket bleibt vermerkt —
   * es existiert weiter; ohne den Vermerk böte die Ticket-Section sonst eine
   * zweite Anlage an. Der Eintrag steht danach wie nach setJiraKey ohne Entscheidung. */
  const clearDecision = useCallback(
    (e: ChangelogEntry) => {
      const key = changelogEntryKey(e);
      setPayload((prev) => {
        const run = { ...(prev.decisions[runId] ?? {}) };
        const existing = run[key];
        if (existing?.jira_key) {
          run[key] = { state: "approved", reviewed_at: existing.reviewed_at, jira_key: existing.jira_key };
        } else {
          delete run[key];
        }
        return { decisions: { ...prev.decisions, [runId]: run } };
      });
    },
    [runId]
  );

  /** Vermerkt die angelegte JIRA-Story; ohne Entscheidung gilt der Eintrag als approved. */
  const setJiraKey = useCallback(
    (e: ChangelogEntry, jiraKey: string) => {
      const key = changelogEntryKey(e);
      setPayload((prev) => {
        const run = { ...(prev.decisions[runId] ?? {}) };
        const existing = run[key];
        run[key] = {
          state: existing?.state ?? "approved",
          reason: existing?.reason,
          reviewed_at: existing?.reviewed_at ?? new Date().toISOString(),
          jira_key: jiraKey,
        };
        return { decisions: { ...prev.decisions, [runId]: run } };
      });
    },
    [runId]
  );

  const counts = useCallback(
    (entries: ChangelogEntry[]) => {
      const out = { open: 0, approved: 0, rejected: 0 };
      for (const e of entries) {
        const d = decisions[changelogEntryKey(e)];
        if (d?.state === "approved") out.approved++;
        else if (d?.state === "rejected") out.rejected++;
        else out.open++;
      }
      return out;
    },
    [decisions]
  );

  const exportState = useCallback(() => JSON.stringify({ decisions }, null, 2), [decisions]);

  return { getDecision, setDecision, clearDecision, setJiraKey, counts, exportState };
}
